import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, User, ArrowLeft, ChevronRight } from 'lucide-react';
import CommitmentSection from '../components/CommitmentSection';
import { optimizeImageUrl } from '../utils/imageOptimizer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/${slug}`);
      if (!response.ok) {
        throw new Error('Post not found');
      }
      const data = await response.json();
      setPost(data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Generate Breadcrumb schema for SEO
  const breadcrumbSchema = post ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.edenremedies.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://www.edenremedies.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://www.edenremedies.com/blog/${post.slug}`
      }
    ]
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f1eb] flex items-center justify-center">
        <div className="animate-pulse text-[#2d4a47]">Loading...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#f5f1eb] flex flex-col items-center justify-center">
        <h1 className="text-2xl font-serif text-gray-900 mb-4">Post Not Found</h1>
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-[#2d4a47] hover:underline"
        >
          <ArrowLeft size={16} /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f1eb]">
      {/* Breadcrumb Schema for SEO */}
      {breadcrumbSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        </Helmet>
      )}

      {/* Header Image */}
      {post.headerImage && (
        <div className="w-full h-[400px] md:h-[500px] overflow-hidden">
          <img
            src={optimizeImageUrl(post.headerImage, { width: 1200 })}
            alt="Natural and organic skincare product"
            className="w-full h-full object-cover"
            width={1200}
            height={500}
            fetchpriority="high"
          />
        </div>
      )}

      {/* Content */}
      <article className="max-w-3xl mx-auto px-4 py-12">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#2d4a47] transition-colors">Home</Link>
          <ChevronRight size={14} />
          <Link to="/blog" className="hover:text-[#2d4a47] transition-colors">Blog</Link>
          <ChevronRight size={14} />
          <span className="text-gray-700 truncate max-w-[200px]">{post.title}</span>
        </nav>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 mb-6">
          {post.title}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-6 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-200">
          <span className="flex items-center gap-2">
            <Calendar size={16} />
            {formatDate(post.createdAt)}
          </span>
          <span className="flex items-center gap-2">
            <User size={16} />
            {post.author}
          </span>
        </div>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-[#2d4a47] prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#2d4a47] font-medium hover:underline"
          >
            <ArrowLeft size={16} /> Back to All Posts
          </Link>
        </div>
      </article>

      {/* Commitment Section */}
      <CommitmentSection variant="box" />
    </div>
  );
};

export default BlogPostPage;
